import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STEP_LINKS = {
    organization: "/settings",
    chart_of_accounts: "/accounting/coa",
    bank_account: "/banking/bank-accounts",
    customers: "/sales/customers",
    items: "/inventory/items",
    first_invoice: "/sales/invoices",
};

export default function OnboardingChecklist() {
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(localStorage.getItem("onboarding_dismissed") === "true");

    useEffect(() => {
        api.get("/onboarding/status").then(({ data }) => setData(data)).catch(() => {});
    }, []);

    if (!data || dismissed || data.org?.completed || data.completed_count === data.total) return null;

    const dismiss = () => {
        localStorage.setItem("onboarding_dismissed", "true");
        setDismissed(true);
    };

    const markComplete = async () => {
        await api.post("/onboarding/complete");
        setData({ ...data, org: { ...data.org, completed: true } });
    };

    return (
        <div className="border border-[#a68a56]/30 rounded-md bg-gradient-to-br from-[#fafaf7] to-white p-5 mb-5 relative" data-testid="onboarding-checklist">
            <button onClick={dismiss} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-md bg-[#a68a56]/12 grid place-items-center shrink-0">
                    <Sparkles className="w-5 h-5 text-[#a68a56]" />
                </div>
                <div className="flex-1">
                    <h3 className="text-base font-semibold">Welcome to Vyaahar — let's get you set up</h3>
                    <p className="text-[12.5px] text-muted-foreground mt-0.5">{data.completed_count} of {data.total} steps complete · {data.progress_pct}%</p>
                </div>
            </div>
            <div className="w-full h-1.5 bg-[#fafaf7] rounded-full overflow-hidden mb-4">
                <div className="h-full bg-[#a68a56] transition-all" style={{ width: `${data.progress_pct}%` }} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.steps.map((s) => (
                    <Link
                        key={s.id}
                        to={STEP_LINKS[s.id] || "/"}
                        data-testid={`onb-step-${s.id}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md border transition ${s.done ? "border-[#1f6c47]/30 bg-[#1f6c47]/8" : "border-border hover:bg-[#fafaf7]"}`}
                    >
                        {s.done ? <CheckCircle2 className="w-4 h-4 text-[#1f6c47]" /> : <Circle className="w-4 h-4 text-muted-foreground" />}
                        <span className={`text-[13px] ${s.done ? "line-through text-muted-foreground" : "font-medium"}`}>{s.label}</span>
                    </Link>
                ))}
            </div>
            {data.completed_count === data.total && (
                <Button className="mt-4 w-full" onClick={markComplete} data-testid="onb-complete">Mark setup complete</Button>
            )}
        </div>
    );
}
